import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Database, Search, Plus, Pencil, Trash2, Loader2, RefreshCw, AlertTriangle, ArrowDownUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 25;

export default function RecordsTab() {
  const [config, setConfig] = useState(null);
  const [env, setEnv] = useState("local");
  const [collection, setCollection] = useState("subscribers");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [keyField, setKeyField] = useState("id");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editor, setEditor] = useState(null); // {mode, json, keyValue}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/records/config`, { credentials: "include" });
        const data = await res.json();
        if (data.success) setConfig(data);
        else toast.error(data.detail || "Failed to load records config");
      } catch (e) { toast.error("Failed to load records config"); }
    })();
  }, []);

  const load = async (newSkip = 0, opts = {}) => {
    const sf = opts.sortField ?? sortField;
    const sd = opts.sortDir ?? sortDir;
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await fetch(`${API}/api/admin/records/list`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ env, collection, search, skip: newSkip, limit: PAGE, sort_field: sf, sort_dir: sd }),
      });
      const data = await res.json();
      if (res.ok) {
        setRecords(data.records || []);
        setTotal(data.total || 0);
        setKeyField(data.key_field || "id");
        setSkip(newSkip);
      } else toast.error(data.detail || "List failed");
    } catch (e) { toast.error("List failed: " + e.message); }
    setLoading(false);
  };

  const openCreate = () => setEditor({ mode: "create", json: "{\n  \n}", keyValue: null });
  const openEdit = (rec) => setEditor({ mode: "edit", json: JSON.stringify(rec, null, 2), keyValue: rec[keyField] });

  const saveEditor = async () => {
    let doc;
    try { doc = JSON.parse(editor.json); }
    catch (e) { return toast.error("Invalid JSON: " + e.message); }
    setSaving(true);
    try {
      const path = editor.mode === "create" ? "/create" : "/update";
      const body = editor.mode === "create"
        ? { env, collection, doc }
        : { env, collection, key_field: keyField, key_value: editor.keyValue, doc };
      const res = await fetch(`${API}/api/admin/records${path}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(editor.mode === "create" ? "Record created" : "Record updated");
        setEditor(null);
        load(skip);
      } else toast.error(data.error || data.detail || "Save failed");
    } catch (e) { toast.error("Save failed: " + e.message); }
    setSaving(false);
  };

  const deleteRecord = async (rec) => {
    const isSub = collection === "subscribers" || collection === "students";
    const cascade = isSub ? window.confirm(`Delete ${rec[keyField]} from ${envLabel()}?\n\nCascade delete their related data (subscriptions, assessments, questions…)?\n\nOK = cascade delete • Cancel = delete this record only`) : false;
    if (!isSub && !window.confirm(`Delete ${rec[keyField]} from ${envLabel()}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/records/delete`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ env, collection, key_field: keyField, key_value: rec[keyField], cascade }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const casc = data.cascade && Object.keys(data.cascade).length
          ? " (cascade: " + Object.entries(data.cascade).map(([k, v]) => `${k}:${v}`).join(", ") + ")" : "";
        toast.success("Deleted" + casc);
        load(skip);
      } else toast.error(data.error || data.detail || "Delete failed");
    } catch (e) { toast.error("Delete failed: " + e.message); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected record(s) from ${envLabel()}?${(collection === "subscribers" || collection === "students") ? "\n\nRelated data will also be cascade-deleted." : ""}\n\nThis cannot be undone.`)) return;
    const isSub = collection === "subscribers" || collection === "students";
    let ok = 0, fail = 0;
    for (const kv of selected) {
      try {
        const res = await fetch(`${API}/api/admin/records/delete`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ env, collection, key_field: keyField, key_value: kv, cascade: isSub }),
        });
        const data = await res.json();
        if (res.ok && data.success) ok++; else fail++;
      } catch { fail++; }
    }
    toast.success(`Deleted ${ok}${fail ? `, ${fail} failed` : ""}`);
    load(skip);
  };

  const toggleSel = (kv) => setSelected((prev) => {
    const n = new Set(prev); n.has(kv) ? n.delete(kv) : n.add(kv); return n;
  });

  const envLabel = () => env === "local" ? `This env (${config?.current_db})` : `Remote (${(config?.remote_url || "").replace(/^https?:\/\//, "")})`;

  if (!config) return <div className="flex items-center gap-2 text-gray-500 p-6" data-testid="records-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  if (!config.control_plane) {
    return <div className="flex items-start gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="records-not-control-plane">
      <AlertTriangle className="h-4 w-4 mt-0.5" /> Record management is only available in the Preview (control-plane) environment.
    </div>;
  }

  const cols = ["name", "phone_number", "phone", "email", "grade", "registration_source", "created_at"];

  return (
    <div className="space-y-4" data-testid="records-tab">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-teal-100"><Database className="h-5 w-5 text-teal-600" /></div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Record Management</h2>
          <p className="text-sm text-gray-500">View, create, edit and delete records in preview or production.</p>
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardHeader><CardTitle className="text-base">Browse Records</CardTitle>
          <CardDescription>Pick an environment and collection, then search.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Environment</p>
              <select value={env} onChange={(e) => setEnv(e.target.value)} data-testid="records-env"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="local">This env ({config.current_db})</option>
                {config.remote_configured && <option value="remote">Remote ({(config.remote_url || "").replace(/^https?:\/\//, "")})</option>}
              </select>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Collection</p>
              <select value={collection} onChange={(e) => setCollection(e.target.value)} data-testid="records-collection"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                {config.collections.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Search</p>
              <div className="flex gap-2">
                <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)}
                  placeholder="name, phone, id, email…" data-testid="records-search-input" />
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button onClick={() => load(0)} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="records-search-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Search
            </Button>
            <div className="flex items-center gap-1">
              <ArrowDownUp className="h-4 w-4 text-gray-400" />
              <select value={sortField} onChange={(e) => { setSortField(e.target.value); load(0, { sortField: e.target.value }); }}
                data-testid="records-sort-field" className="border border-gray-200 rounded-lg px-2 py-2 text-sm">
                <option value="name">Sort by Name</option>
                <option value="id">Sort by ID</option>
              </select>
              <select value={sortDir} onChange={(e) => { setSortDir(e.target.value); load(0, { sortDir: e.target.value }); }}
                data-testid="records-sort-dir" className="border border-gray-200 rounded-lg px-2 py-2 text-sm">
                <option value="asc">A → Z (asc)</option>
                <option value="desc">Z → A (desc)</option>
              </select>
            </div>
            <Button onClick={openCreate} variant="outline" data-testid="records-add-btn"><Plus className="h-4 w-4 mr-2" /> Add record</Button>
            {selected.size > 0 && (
              <Button onClick={deleteSelected} variant="outline" className="border-red-300 text-red-600" data-testid="records-delete-selected-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete selected ({selected.size})
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
              <Badge variant="outline">{envLabel()}</Badge>
              {total > 0 && <span data-testid="records-total">{total} total</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {records.length > 0 && (
        <Card className="bg-white border-gray-200" data-testid="records-results">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="py-2 px-3"></th>
                <th className="py-2 px-3">{keyField}</th>
                {cols.map((c) => <th key={c} className="py-2 px-3">{c}</th>)}
                <th className="py-2 px-3">Actions</th>
              </tr></thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r[keyField] || i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`records-row-${r[keyField]}`}>
                    <td className="py-1.5 px-3"><input type="checkbox" checked={selected.has(r[keyField])} onChange={() => toggleSel(r[keyField])} disabled={r[keyField] == null} /></td>
                    <td className="py-1.5 px-3 font-medium text-gray-800">{String(r[keyField] ?? "—")}</td>
                    {cols.map((c) => <td key={c} className="py-1.5 px-3 text-gray-600 truncate max-w-[160px]">{r[c] != null ? String(r[c]) : "—"}</td>)}
                    <td className="py-1.5 px-3">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="Edit" data-testid={`records-edit-${r[keyField]}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => deleteRecord(r)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete" data-testid={`records-delete-${r[keyField]}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {records.length === 0 && total === 0 && !loading && (
        <p className="text-sm text-gray-400 px-1" data-testid="records-empty">No records loaded. Choose a collection and click Search.</p>
      )}

      {total > PAGE && (
        <div className="flex items-center gap-2 justify-end">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => load(Math.max(0, skip - PAGE))} data-testid="records-prev">Prev</Button>
          <span className="text-sm text-gray-500">{skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
          <Button variant="outline" size="sm" disabled={skip + PAGE >= total} onClick={() => load(skip + PAGE)} data-testid="records-next">Next</Button>
        </div>
      )}

      {/* Editor modal */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editor?.mode === "create" ? "Create record" : "Edit record"} — {collection} ({envLabel()})</DialogTitle></DialogHeader>
          <Textarea value={editor?.json || ""} onChange={(e) => setEditor({ ...editor, json: e.target.value })}
            className="font-mono text-xs h-[420px]" data-testid="records-editor-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} data-testid="records-editor-cancel">Cancel</Button>
            <Button onClick={saveEditor} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="records-editor-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
